import type { VanDerValkSuitabilityConfig } from './types'
import {
  getDiscriminatingVariantFieldKeys,
  serializeSuitabilityFieldValue,
} from './suitabilityOptions'

/** Select value when no suitable system row is chosen yet. */
export const VDV_SUITABILITY_UNSELECTED = ''

/** Stable `Select` value for a row at `index` in the current suitability result list. */
export function suitabilityRowSelectValue(index: number): string {
  return `vdv-row-${index}`
}

/** Resolve a flat-dropdown `Select` value back to a suitability row, or null for the sentinel. */
export function parseSuitabilityRowSelectValue(
  value: string,
  rows: VanDerValkSuitabilityConfig[]
): VanDerValkSuitabilityConfig | null {
  if (value === VDV_SUITABILITY_UNSELECTED) {
    return null
  }
  const match = /^vdv-row-(\d+)$/.exec(value)
  if (!match) {
    return null
  }
  const idx = Number(match[1])
  if (!Number.isFinite(idx) || idx < 0 || idx >= rows.length) {
    return null
  }
  return rows[idx] ?? null
}

/** Human-friendly orientation, e.g. LANDSCAPE -> Landscape. */
function formatModuleOrientationForLabel(raw: string): string {
  if (!raw) return ''
  return raw
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** Core grouping: name / system / placeSystem / orientation. */
export function baseRowLabel(row: VanDerValkSuitabilityConfig): string {
  const orientation = formatModuleOrientationForLabel(String(row.moduleOrientation))
  return [row.name, row.system, row.placeSystem, orientation].filter(Boolean).join(' / ')
}

/** Dropdown / group key: backend `displayLabel` when present, else derived `baseRowLabel`. */
export function suitabilityGroupLabel(row: VanDerValkSuitabilityConfig): string {
  const fromBackend = row.displayLabel?.trim()
  if (fromBackend) {
    return fromBackend
  }
  return baseRowLabel(row)
}

/**
 * Group suitability rows by `suitabilityGroupLabel`, preserving first-seen order.
 */
export function groupSuitabilityRowsByBaseLabel(rows: VanDerValkSuitabilityConfig[]): {
  order: string[]
  groups: Map<string, VanDerValkSuitabilityConfig[]>
} {
  const order: string[] = []
  const groups = new Map<string, VanDerValkSuitabilityConfig[]>()
  for (const row of rows) {
    const k = suitabilityGroupLabel(row)
    if (!groups.has(k)) {
      order.push(k)
      groups.set(k, [])
    }
    groups.get(k)!.push(row)
  }
  return { order, groups }
}

function sameSuitabilityBaseIdentity(
  a: VanDerValkSuitabilityConfig,
  b: VanDerValkSuitabilityConfig
): boolean {
  const aLabel = a.displayLabel?.trim()
  const bLabel = b.displayLabel?.trim()
  if (aLabel && bLabel) {
    return aLabel === bLabel
  }
  return a.name === b.name && a.system === b.system && a.placeSystem === b.placeSystem
}

/**
 * Resolve the base-system dropdown key when ``selectedSuitability`` is a partial persisted row
 * (variant picks merged onto a vendor row) that no longer matches any flattened result row exactly.
 */
export function resolveSuitabilityGroupKeyForSelection(
  selected: VanDerValkSuitabilityConfig,
  groups: Map<string, VanDerValkSuitabilityConfig[]>,
  order: string[]
): string {
  const direct = suitabilityGroupLabel(selected)
  if (groups.has(direct)) {
    return direct
  }
  for (const key of order) {
    const rows = groups.get(key)
    if (rows?.some((r) => sameSuitabilityBaseIdentity(r, selected))) {
      return key
    }
  }
  return direct
}

/** i18n key for the InputLabel of each variant (discriminator) field. */
export const MOUNT_VDV_FIELD_LABEL_KEY: Record<keyof VanDerValkSuitabilityConfig, string | undefined> = {
  displayLabel: undefined,
  name: 'mount_vdv_name',
  system: 'mount_vdv_system',
  vandervalkExecution: 'mount_vdv_execution',
  placeSystem: 'mount_vdv_place_system',
  moduleOrientation: 'mount_vdv_module_orientation',
  rowPitch: 'mount_vdv_row_pitch',
  gap: 'mount_vdv_gap',
  tilt: 'mount_vdv_tilt',
  dilatationMaxColumns: 'mount_vdv_dilatation_max_columns',
  dilatationMaxRows: 'mount_vdv_dilatation_max_rows',
  dilatationMaxColumnsLength: 'mount_vdv_dilatation_max_columns_length',
  dilatationMaxRowsLength: 'mount_vdv_dilatation_max_rows_length',
  frontPath: 'mount_vdv_front_path',
  backPath: 'mount_vdv_back_path',
  pvModuleLift: 'mount_vdv_pv_module_lift',
  color: 'mount_vdv_color',
  fixation: 'mount_vdv_fixation',
  longProfileType: 'mount_vdv_long_profile_type',
  profileLength: 'mount_vdv_profile_length',
  hangerBolt: 'mount_vdv_hanger_bolt',
  roofHookType: 'mount_vdv_roof_hook_type',
  longProfile: 'mount_vdv_long_profile',
  screwType: 'mount_vdv_screw_type',
  seamClampType: 'mount_vdv_seam_clamp_type',
  mountingBracketBoltSize: 'mount_vdv_mounting_bracket_bolt_size',
  maxCtcDistance: 'mount_vdv_max_ctc_distance',
  anchorType: 'mount_vdv_anchor_type',
  fastenerType: 'mount_vdv_fastener_type',
  roofMaterial: 'mount_vdv_roof_material',
  roofConstructionHeight: 'mount_vdv_roof_construction_height',
  availableScrewTypes: undefined,
  restrictPlacementToSubstructure: undefined,
  warnings: undefined,
}

const MM_FIELDS: (keyof VanDerValkSuitabilityConfig)[] = [
  'rowPitch',
  'gap',
  'dilatationMaxColumnsLength',
  'dilatationMaxRowsLength',
  'frontPath',
  'backPath',
  'pvModuleLift',
  'profileLength',
  'maxCtcDistance',
  'roofConstructionHeight',
]

/** Pitched-roof enum-ish fields whose UPPER_SNAKE values are shown Title-cased (e.g. HANGER_BOLT -> Hanger Bolt). */
const TITLE_CASE_FIELDS: (keyof VanDerValkSuitabilityConfig)[] = [
  'color',
  'fixation',
  'longProfileType',
  'roofHookType',
  'seamClampType',
  'anchorType',
  'fastenerType',
  'roofMaterial',
]

function toTitleCaseFromEnum(raw: string): string {
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

const ROOF_HOOK_TYPE_LABEL: Record<string, string> = {
  STRONG: 'Strongline',
  STRONG_HEAVY: 'Strongline Heavy Duty',
  TILE: 'Plain Tile',
  SLATE: 'Slate Tile',
}

const COLOR_LABEL: Record<string, string> = {
  ALU: 'Aluminium',
}

/**
 * Display a single serialised field value in a small variant dropdown (adds mm/° where appropriate).
 */
export function formatVdvFieldOptionValue(field: keyof VanDerValkSuitabilityConfig, serialized: string): string {
  if (serialized === '') {
    return '—'
  }
  if (field === 'moduleOrientation') {
    return formatModuleOrientationForLabel(serialized)
  }
  if (field === 'maxCtcDistance') {
    try {
      const parsed = JSON.parse(serialized) as { min?: number; max?: number }
      if (typeof parsed.min === 'number' && typeof parsed.max === 'number') {
        return parsed.min === parsed.max ? `${parsed.max} mm` : `${parsed.min}–${parsed.max} mm`
      }
    } catch {
      /* scalar mm */
    }
  }
  if (field === 'tilt') {
    return `${serialized}°`
  }
  if (MM_FIELDS.includes(field)) {
    return `${serialized} mm`
  }
  if (field === 'color' && COLOR_LABEL[serialized]) {
    return COLOR_LABEL[serialized]
  }
  if (field === 'roofHookType' && ROOF_HOOK_TYPE_LABEL[serialized]) {
    return ROOF_HOOK_TYPE_LABEL[serialized]
  }
  if (TITLE_CASE_FIELDS.includes(field)) {
    return toTitleCaseFromEnum(serialized)
  }
  return serialized
}

/**
 * Label for a row in the flat suitable-systems dropdown. When several rows share the same group
 * label, append discriminating variant fields so each option is unique.
 */
export function formatSuitabilityRowOptionLabel(
  row: VanDerValkSuitabilityConfig,
  allRows: VanDerValkSuitabilityConfig[]
): string {
  const base = suitabilityGroupLabel(row)
  const siblings = allRows.filter((r) => suitabilityGroupLabel(r) === base)
  if (siblings.length <= 1) {
    return base
  }
  const discriminating = getDiscriminatingVariantFieldKeys(siblings)
  const suffixParts = discriminating
    .map((field) => {
      const serialized = serializeSuitabilityFieldValue(row[field])
      if (!serialized) {
        return null
      }
      return formatVdvFieldOptionValue(field, serialized)
    })
    .filter((part): part is string => part != null && part !== '—')
  if (suffixParts.length === 0) {
    return base
  }
  return `${base} (${suffixParts.join(', ')})`
}
